"use client";

import { signOut, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useEffect, useState } from "react";
import { api } from "~/trpc/react";
import { Button, buttonVariants } from "~/components/ui/button";

export function ConversionHandler({
  intendedRole,
}: {
  intendedRole: string | null;
}) {
  const { status, data } = useSession();
  const router = useRouter();
  const [hasHandledConversion, setHasHandledConversion] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const startTherapistOnboarding =
    api.therapistRouter.startTherapistOnboarding.useMutation();

  useEffect(() => {
    if (status === "authenticated" && data?.user && !hasHandledConversion) {
      const storedRole = localStorage.getItem("intendedRole");

      // Explicit therapist entry must not convert an authenticated patient.
      if (intendedRole === "therapist" && data.user.role === "PATIENT") {
        localStorage.removeItem("intendedRole");
        setHasHandledConversion(true);
        router.push("/app/inbox");
        return;
      }

      const role = intendedRole ?? storedRole;

      if (role === "therapist" && data.user.role === "PATIENT") {
        setHasHandledConversion(true);

        startTherapistOnboarding.mutate(undefined, {
          onSuccess: () => {
            localStorage.removeItem("intendedRole");
            window.location.href = "/app/onboarding/terapeut";
          },
          onError: (error) => {
            console.error("Failed to convert to therapist:", error);
            if (error.message === "PATIENT_HISTORY_EXISTS") {
              localStorage.removeItem("intendedRole");
              return;
            }
            if (error.message === "PATIENT_PROFILE_MISSING") {
              setConversionError(
                "Nedostaje profil pacijenta. Pokušaj ponovo. Ako se poruka ponovi, kontaktiraj podršku.",
              );
              return;
            }
            setConversionError("Nije moguće otvoriti prijavu za terapeuta.");
          },
        });
        return;
      }

      if (data.user.role === "THERAPIST") {
        router.push("/app/dashboard");
      } else {
        router.push("/app/inbox");
      }

      if (storedRole) {
        localStorage.removeItem("intendedRole");
      }
    }
  }, [
    status,
    data,
    intendedRole,
    hasHandledConversion,
    startTherapistOnboarding,
    router,
  ]);

  if (startTherapistOnboarding.error?.message === "PATIENT_HISTORY_EXISTS") {
    return (
      <main className="mx-auto flex min-h-screen max-w-lg items-center px-4">
        <section
          role="alert"
          className="border-border bg-card rounded-lg border p-6 shadow-sm"
        >
          <h1 className="text-xl font-semibold">
            Ne možemo da otvorimo terapeut nalog
          </h1>
          <p className="text-muted-foreground mt-3 text-sm">
            Tvoji postojeći podaci pacijenta su sačuvani. Odjavi se i koristi
            poseban email i nalog za terapeuta. Nijedan podatak se ne briše.
          </p>
          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <Button
              disabled={isSigningOut}
              onClick={() => {
                setIsSigningOut(true);
                void signOut({ callbackUrl: "/app/login?role=therapist" });
              }}
            >
              {isSigningOut
                ? "Odjavljivanje..."
                : "Odjavi se i napravi poseban nalog"}
            </Button>
            <Link
              className={
                buttonVariants({ variant: "outline" }) +
                " text-center whitespace-normal sm:whitespace-nowrap"
              }
              href="/app/inbox"
            >
              Nastavi kao pacijent
            </Link>
          </div>
        </section>
      </main>
    );
  }
  if (conversionError) {
    return (
      <main className="mx-auto flex min-h-screen max-w-lg items-center px-4">
        <section
          role="alert"
          className="bg-card rounded-lg border p-6 shadow-sm"
        >
          <h1 className="text-xl font-semibold">Nešto nije uspelo</h1>
          <p className="text-muted-foreground mt-3 text-sm">
            {conversionError} Proveri vezu i pokušaj ponovo.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button
              onClick={() => {
                setConversionError(null);
                setHasHandledConversion(false);
              }}
            >
              Pokušaj ponovo
            </Button>
            <Link
              className={buttonVariants({ variant: "outline" })}
              href="/app/inbox"
            >
              Nastavi kao pacijent
            </Link>
          </div>
        </section>
      </main>
    );
  }
  if (status !== "unauthenticated")
    return (
      <main
        className="flex min-h-screen items-center justify-center"
        role="status"
        aria-live="polite"
      >
        {hasHandledConversion
          ? "Pripremamo tvoju prijavu..."
          : "Otvaramo tvoj nalog..."}
      </main>
    );
  return null;
}
